import { isLiquityFamily } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Liquity V2 family (and its seven friendly forks) — pooled CDPs where the
 * BORROWER sets the interest rate.
 *
 * Redemption terms audited in CDP_REDEMPTION_TERMS.md.
 *
 * The term nobody expects: **redemption**. Your collateral can be taken at par
 * while the trove is perfectly healthy, purely because you carry the lowest
 * rate on the branch. It is not a liquidation and no health factor predicts
 * it, so it needs its own field (`liquidation.redeemable`) and its own
 * critical finding.
 */
export const liquityAdapter: TermAdapter = {
  id: 'liquity',
  matches: isLiquityFamily,
  profileId: () => 'liquity.cdp@v1',
  build: (input) => {
    const liquity = (input.market?.liquity ?? {}) as Record<string, unknown>
    const minDebt =
      liquity.minDebt != null ? String(liquity.minDebt) : undefined
    const minApr =
      typeof liquity.minAnnualInterestRate === 'string' ||
      typeof liquity.minAnnualInterestRate === 'number'
        ? (Number(liquity.minAnnualInterestRate) / 1e18) * 100
        : undefined
    const maxApr =
      typeof liquity.maxAnnualInterestRate === 'string' ||
      typeof liquity.maxAnnualInterestRate === 'number'
        ? (Number(liquity.maxAnnualInterestRate) / 1e18) * 100
        : undefined

    // A branch shuts down PERMANENTLY on TCR < SCR or oracle failure, and that
    // flips the redemption terms below. Truthy-checked rather than compared to
    // `true`: the descriptor carries whatever the metadata generator wrote, and
    // upstream reads it off `TroveManager.shutdownTime()` — a TIMESTAMP, where
    // `0` means live and any non-zero value means shut.
    const shutdown = Boolean(liquity.shutdown)

    const wadPct = (v: unknown) => (v == null ? undefined : Number(v) / 1e18)
    const spPenalty = wadPct(liquity.liquidationPenaltySP)
    const redistPenalty = wadPct(liquity.liquidationPenaltyRedistribution)
    const penalties = [
      spPenalty != null && Number.isFinite(spPenalty)
        ? {
            id: 'stability-pool',
            label: 'Stability-Pool liquidation',
            value: spPenalty,
            description:
              'Charged when the Stability Pool absorbs the debt — the normal path.',
          }
        : null,
      redistPenalty != null && Number.isFinite(redistPenalty)
        ? {
            id: 'redistribution',
            label: 'Redistribution liquidation',
            value: redistPenalty,
            description:
              'Charged when the Stability Pool is empty and the debt is redistributed across the branch’s other troves. Materially worse than the Stability-Pool path.',
          }
        : null,
    ].filter(Boolean) as NonNullable<NonNullable<ReturnType<typeof Object>>>[]

    // A branch emits TWO rows and they are opposite products, so one `supply`
    // block cannot describe both. This adapter asserted the Stability Pool's
    // terms on BOTH, which told a trove holder their collateral was an
    // instant-exit yield position carrying someone else's bad debt — across 9
    // deployments and 45 branches.
    //
    //  - COLLATERAL row (`collateralActive: true`): trove collateral. Earns
    //    nothing, and cannot be posted alone — `openTrove` refuses anything
    //    under `minDebt`, so there is no zero-debt trove to hold it.
    //  - DEBT row: the Stability Pool. A real earn position, first in line for
    //    liquidation losses, and openable on its own (`provideToSP` needs no
    //    trove).
    const isCollateralRow = input.collateralActive !== false

    return {
      supply: isCollateralRow
        ? {
            role: 'collateral',
            availability: { opensWith: 'both-legs' },
            counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
          }
        : {
            // The Stability Pool is an earn position, never collateral.
            role: 'yield',
            exit: { mode: 'instant', settlement: 'sync', partialAllowed: true },
            availability: { opensWith: 'standalone' },
            counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
            principal: { protected: false, risks: ['bad-debt'] },
          },
      borrow: {
        // `openTrove` takes collateral, debt AND the interest rate together, so
        // a bare borrow has no trove to draw against. Reaches the debt row
        // only — the collateral row has no borrow side to refine.
        availability: { opensWith: 'both-legs' },
        rate: {
          kind: 'user-set',
          isLocked: false,
          source: 'borrower',
          minApr,
          maxApr,
          // The contract a rate-setter UI needs. Without `required: true` a
          // borrow form has no way to know an input is mandatory here — and
          // the choice is not cosmetic: the rate decides your place in the
          // redemption queue, so the cheapest is also the first redeemed.
          userSet: {
            required: true,
            min: minApr,
            max: maxApr,
            // The action applies the branch average when no rate is passed,
            // so pre-filling it keeps the UI and the builder in agreement.
            default: input.variableBorrowRate,
            adjustable: true,
            adjustmentCostNote:
              'Changing the rate re-charges the upfront fee (roughly a week of interest), so it is not free.',
            adjustmentCooldownSecs:
              typeof liquity.interestRateAdjCooldownSeconds === 'number'
                ? liquity.interestRateAdjCooldownSeconds
                : undefined,
          },
        },
        debtShape: 'accruing',
        exit: {
          earlyRepay: 'free',
          atMaturityCost: 'accrued',
          lateBehaviour: 'none',
          partialAllowed: true,
          minDebt,
        },
        liquidation: {
          // The Stability Pool absorbs the debt first; only if it cannot does
          // the debt get REDISTRIBUTED across the branch's other troves — and
          // the two paths charge different penalties, which a single `penalty`
          // cannot express.
          model: 'stability-pool',
          absorber: 'stability-pool',
          trigger: 'price',
          seizure: 'proportional',
          penalties: penalties.length ? penalties : undefined,
          badDebt: 'redistributed',
          redeemable: true,
          // Two different mechanisms share the word "redemption", and the
          // ordinary one stops applying the moment a branch shuts down.
          //
          // NORMAL: rate-ordered, fee stays in the trove, borrower ~USD-neutral.
          // SHUTDOWN (TCR < SCR or oracle failure, permanent): **urgent
          // redemptions** run at a 2 % collateral BONUS to the redeemer, against
          // any trove — so the queue position a borrower bought with their rate
          // is worthless, and they are no longer neutral, they are down 2 %.
          //
          // Asserting the normal terms unconditionally told exactly the wrong
          // story to the only borrowers who could still act on it.
          redemption: shutdown
            ? {
                trigger: 'permissionless-arbitrage',
                // No longer peg-driven: the 2 % bonus pays regardless.
                driver: 'always',
                // Urgent redemptions ignore the interest-rate ordering.
                order: 'pro-rata',
                valueImpact: 'loss',
                defence:
                  'This branch is shut down and urgent redemptions pay a 2 % collateral bonus, so your interest rate no longer protects you. Closing or repaying the position is the only way out from under it.',
              }
            : {
                // Not governance and not a liquidation: any holder of the
                // stable can trigger it, without permission.
                trigger: 'permissionless-arbitrage',
                // Permissionless does NOT mean constant — it only pays while
                // the stable trades under target, which is the point:
                // redemptions are what push it back up.
                driver: 'below-peg',
                // The rate you chose IS your position in the queue.
                order: 'lowest-rate-first',
                // The redemption fee stays in the trove as extra collateral, so
                // the borrower is ~USD-neutral; what is lost is EXPOSURE.
                valueImpact: 'usd-neutral',
                // The ordering is already stated by `order`; say what to DO —
                // including the trap that follows a partial redemption.
                defence:
                  'Raising your rate moves you back in that queue. If a redemption leaves you below the minimum debt your trove goes ZOMBIE — removed from the queue, and only recoverable by topping it back above the floor or closing it.',
              },
        },
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      constraints: {
        positionModel: 'cdp-id',
        positionIdMeaning: 'The trove id (a keccak of owner + branch + index).',
      },
    }
  },
}
